import _ from 'lodash';
import React from 'react';
import { FormControl, InputLabel, Select, SelectProps, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import MuiMenuItem from '@mui/material/MenuItem';

import clsx from 'clsx';
import { TOption } from 'src/types/Commons/option.model';
import { Controller, FieldName, FieldValues, FormState, Path, UseFormRegister } from 'react-hook-form';
import { FormHookProps } from 'src/types/Commons/form-hook';

// eslint-disable-next-line @typescript-eslint/no-explicit-any

const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(2)
  }
}));

function _SelectFieldFormHook(props: any) {
  const importedProps = _.pick(props, [
    'autoComplete',
    'autoFocus',
    'children',
    'className',
    'defaultValue',
    'disabled',
    'FormHelperTextProps',
    'fullWidth',
    'id',
    'InputLabelProps',
    'inputProps',
    'InputProps',
    'inputRef',
    'label',
    'multiline',
    'multiple',
    'name',
    'onBlur',
    'onChange',
    'onFocus',
    'placeholder',
    'required',
    'rows',
    'rowsMax',
    'select',
    'SelectProps',
    'type',
    'variant',
    'value'
  ]);

  const {
    errorMessage,
    register,
    options,
    name,
    variant,
    id,
    styleFormControl,
    dataOptions,
    setValue,
    onChangeValue,
    size
  } = props;

  function handleChange(value: any) {
    setValue && setValue(name, value?.props.value);
    onChangeValue && onChangeValue(value?.props.value);
  }

  return (
    <>
      <FormControl fullWidth variant={variant} size={size} className={clsx(styleFormControl)}>
        {props.label && <InputLabel id={id}>{props.label}</InputLabel>}
        <Select
          {...register(name, options)}
          {...importedProps}
          labelId={props.label}
          onChange={(e, options) => handleChange(options)}
          error={Boolean((!props.isPristine && props.showRequired) || (errorMessage ? true : false))}
        >
          {dataOptions &&
            dataOptions.map((option: { value: number | string; label: string; icon?: any }, index: number) => {
              return (
                <MenuItem value={option.value} key={index}>
                  {option.icon && option.icon}
                  {option.label}
                </MenuItem>
              );
            })}
        </Select>
      </FormControl>

      {errorMessage && (
        <Typography variant="inherit" className="text-red-500">
          {errorMessage}
        </Typography>
      )}
    </>
  );
}
const SelectFieldFormHook = React.memo(_SelectFieldFormHook) as typeof _SelectFieldFormHook;
export default SelectFieldFormHook;

export interface SelectFieldFormHookV2Props<TField extends FieldValues>
  extends FormHookProps<TField>,
    SelectProps<TOption> {
  name: Path<TField>;
  isMulti?: boolean;
  isClearable?: boolean;
  onSelected?: (event: any) => void;
  onInputChange?: (event: any) => void;
  dataOptions?: TOption[];
  label: string;
  [key: string]: any;
  styleFormControl?: { [key: string]: string };
}
function _SelectFieldFormHookV2<TFieldValues extends FieldValues = FieldValues>(
  props: SelectFieldFormHookV2Props<TFieldValues>
) {
  const handleSelected = (event: any) => {
    props.onSelected && props.onSelected(event);
  };
  const { error } = props.control.getFieldState(props.name);
  return (
    <>
      <FormControl fullWidth variant={props.variant} size={props.size} className={clsx(props.styleFormControl)}>
        {props.label && <InputLabel id={props.id}>{props.label}</InputLabel>}
        <Controller
          control={props.control}
          name={props.name}
          rules={props.rules}
          render={({ field: { onChange, value, name, ref }, fieldState: { error } }) => {
            return (
              <Select
                {...props}
                ref={ref}
                name={name}
                value={value}
                labelId={props.label}
                onChange={(e, o) => {
                  handleSelected(e.target.value);
                  onChange(e.target.value);
                }}
                error={Boolean((!props.isPristine && props.showRequired) || (error ? true : false))}
                defaultValue={value}
              >
                {props.dataOptions &&
                  props.dataOptions.map(
                    (option: { value: number | string; label: string; icon?: any }, index: number) => {
                      return (
                        <MenuItem value={option.value} key={index}>
                          {option.icon && option.icon}
                          {option.label}
                        </MenuItem>
                      );
                    }
                  )}
              </Select>
            );
          }}
        />
      </FormControl>

      {props.errorMessage ||
        (error?.message && (
          <Typography variant="inherit" className="text-red-500">
            {props.errorMessage ?? error.message}
          </Typography>
        ))}
    </>
  );
}
export const SelectFieldFormHookV2 = React.memo(_SelectFieldFormHookV2) as typeof _SelectFieldFormHookV2;
